import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Popover, Progress, Spin, Tag } from 'antd';
import { AimOutlined, ArrowLeftOutlined, DownOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { reportsApi } from '../../../utils/api';
import { getCachedMyGoal, peekMyGoalCache, setMyGoalCache } from '../../../utils/myGoalCache';
import { useLiveNotificationFeed } from '../../../hooks/useLiveNotificationFeed';
import { formatReportMoneyFa, formatReportNumberFa } from '../../../pages/reports/chartFormatters';
import { toNumber } from '../../../pages/reports/reportUtils';
import { toPersianDigits } from '../../../utils/formatPriceToman';
import { useAuth } from '../../../contexts/AuthContext';
import { GoalPaceCallout, resolveGoalPaceInfo } from './goalPaceUtils';
import { GoalActionPlanCard, resolveActionPlan } from './GoalActionPlanCard';

const JALALI_MONTHS = {
  '01': 'فروردین',
  '02': 'اردیبهشت',
  '03': 'خرداد',
  '04': 'تیر',
  '05': 'مرداد',
  '06': 'شهریور',
  '07': 'مهر',
  '08': 'آبان',
  '09': 'آذر',
  10: 'دی',
  11: 'بهمن',
  12: 'اسفند',
};

const formatRate = (value, digits = 1) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('fa-IR', {
    minimumFractionDigits: n % 1 === 0 ? 0 : digits,
    maximumFractionDigits: digits,
  })}٪`;
};

/** نمایش فشرده مبلغ: ۳۶۰M / ۵۰۰M */
const formatCompactMoney = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return toPersianDigits('0');
  if (Math.abs(n) >= 1_000_000_000) {
    const v = n / 1_000_000_000;
    const label = Number.isInteger(v) ? String(v) : v.toFixed(1).replace(/\.0$/, '');
    return `${toPersianDigits(label)}B`;
  }
  if (Math.abs(n) >= 1_000_000) {
    const v = n / 1_000_000;
    const label = Number.isInteger(v) ? String(v) : v.toFixed(1).replace(/\.0$/, '');
    return `${toPersianDigits(label)}M`;
  }
  if (Math.abs(n) >= 1_000) {
    const v = n / 1_000;
    const label = Number.isInteger(v) ? String(v) : v.toFixed(1).replace(/\.0$/, '');
    return `${toPersianDigits(label)}K`;
  }
  return toPersianDigits(String(Math.round(n)));
};

const isMoneyGoal = (key) => key === 'revenue';

const resolveMonthName = (period) => {
  const key = String(period?.period_key || '');
  const mFromKey = key.match(/^\d{4}-(\d{2})$/);
  if (mFromKey) return JALALI_MONTHS[mFromKey[1]] || '';
  const start = String(period?.month_start_jalali || '');
  const parts = start.split(/[/\-]/);
  if (parts.length >= 2) {
    const mm = parts[1].padStart(2, '0');
    return JALALI_MONTHS[mm] || '';
  }
  return '';
};

const strokeForRate = (rate) => {
  if (rate >= 100) return '#10b981';
  if (rate >= 80) return '#0D887E';
  if (rate >= 60) return '#f59e0b';
  return '#f43f5e';
};

const StatRow = ({ label, value, emphasize = false }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100/80 dark:border-slate-700/60 last:border-0">
    <span className="text-[12px] text-slate-500 dark:text-slate-400 shrink-0">{label}</span>
    <span
      className={`tabular-nums text-[12.5px] text-left ${
        emphasize
          ? 'font-semibold text-slate-900 dark:text-slate-50'
          : 'font-medium text-slate-700 dark:text-slate-200'
      }`}
    >
      {value}
    </span>
  </div>
);

/** پاسخ API: { status, data } — پاسخ سوکت: { status, data, timestamp } */
const readGoalData = (res) => {
  if (res && typeof res === 'object' && Object.prototype.hasOwnProperty.call(res, 'data')) {
    return res.data ?? null;
  }
  return res ?? null;
};

/**
 * ویجت سراسری «هدف من» در هدر ERP
 * Compact chip + Popover خلاصه (بدون ناوبری مستقیم)
 *
 * داده به صورت زنده از karmadws می‌آید (subscribe-my-goal / رویداد my_goal): karmadws همان
 * GET /reports/goals/my-goal را با توکن کاربر صدا می‌زند و با هر تغییر هدف/معامله/پیگیری دوباره می‌فرستد.
 * با remount شدن هدر در هر ناوبری درخواستی به API زده نمی‌شود؛ اگر سوکت در دسترس نباشد،
 * از API گرفته می‌شود ولی از کش ۵ دقیقه‌ای (myGoalCache) — پس ناوبری باز هم درخواست تکراری نمی‌زند.
 * کش با ورود/خروج و ذخیره هدف (GoalsPanel) باطل می‌شود.
 */
const HeaderMyGoalWidget = ({ isDarkMode = false }) => {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const enabled = Boolean(token && isAuthenticated);
  const [cachedAtMount] = useState(() => peekMyGoalCache());
  const [loading, setLoading] = useState(cachedAtMount === undefined);
  const [switchingGoal, setSwitchingGoal] = useState(false);
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(cachedAtMount ?? null);
  /** کاربر هدف هم‌زمان دیگری انتخاب کرده؛ مثل قبل تا remount بعدی همان نمایش داده می‌شود */
  const switchedGoalRef = useRef(false);

  const applyGoal = useCallback((res) => {
    const data = readGoalData(res);
    // پاسخ API خودش از getCachedMyGoal آمده و کش را به‌روز کرده؛ بازنویسی، زمان کش را تمدید می‌کرد
    if (res?.source !== 'api') setMyGoalCache(data);
    setLoading(false);
    if (!switchedGoalRef.current) setPayload(data);
  }, []);

  const fetchMyGoal = useCallback(async () => {
    try {
      const data = await getCachedMyGoal(() => reportsApi.getMyGoal());
      return { status: 'success', data, source: 'api' };
    } catch (err) {
      console.error('Header goal load failed:', err);
      setLoading(false);
      if (!switchedGoalRef.current) setPayload(null);
      throw err;
    }
  }, []);

  useLiveNotificationFeed({
    subscribeEvent: 'subscribe-my-goal',
    event: 'my_goal',
    fetchList: fetchMyGoal,
    onResponse: applyGoal,
    pollIntervalMs: null, // بدون سوکت: یک بار در هر mount، آن هم از کش اگر تازه باشد
    enabled,
    label: 'My goal',
  });

  // خروج کاربر
  useEffect(() => {
    if (!enabled) {
      switchedGoalRef.current = false;
      setPayload(null);
      setLoading(false);
    }
  }, [enabled]);

  const switchConcurrentGoal = useCallback(async (goalId) => {
    if (goalId == null) return;
    setSwitchingGoal(true);
    try {
      const res = await reportsApi.getMyGoal({ goal_id: goalId });
      switchedGoalRef.current = true;
      setPayload(readGoalData(res));
    } catch (err) {
      console.error('Header concurrent goal switch failed:', err);
    } finally {
      setSwitchingGoal(false);
    }
  }, []);

  const summary = useMemo(() => {
    const header = payload?.header_goal;
    const teamHeader =
      payload?.team_header_goal ||
      (header?.team?.has_goal !== false && header?.team?.achievement_rate != null
        ? {
            ...header.team,
            has_goal: true,
            team_name: header.team.team_name || header.team.name,
            display_text: header.team.display_text,
            achievement_rate: header.team.achievement_rate,
            achievement_rate_display: header.team.achievement_rate_display,
          }
        : null);
    const myTeam = payload?.my_team || null;
    const hasPersonal = Boolean(header?.has_goal);
    const hasTeam = Boolean(teamHeader?.has_goal);
    if (!hasPersonal && !hasTeam) return null;

    const period = payload?.period || {};
    const month = payload?.month || {};
    const key = header?.key || 'revenue';
    const metric = month[key] || {};
    const money = isMoneyGoal(key);

    const actual = toNumber(header?.actual_value ?? metric.actual);
    const target = toNumber(header?.target_value ?? metric.target);
    const rate = toNumber(header?.achievement_rate ?? metric.achievement_rate);
    const projected = toNumber(metric.projected);
    const daysRemaining = toNumber(period.days_remaining);
    const daysPassed = toNumber(period.days_passed);
    const daysTotal =
      toNumber(period.days_total) ||
      (daysPassed || daysRemaining ? daysPassed + daysRemaining : 0);
    const timeElapsedPct = (() => {
      const fromApi = toNumber(
        header?.time_elapsed_rate ?? metric.time_elapsed_rate ?? period.time_elapsed_rate,
      );
      if (fromApi > 0) return Math.min(100, Math.max(0, fromApi));
      if (daysTotal > 0) return Math.min(100, Math.max(0, (daysPassed / daysTotal) * 100));
      return null;
    })();
    const progressIndex = (() => {
      const fromApi = header?.progress_index ?? metric.progress_index ?? period.progress_index;
      if (fromApi != null && fromApi !== '') return toNumber(fromApi);
      if (timeElapsedPct > 0 && rate >= 0) return (rate / timeElapsedPct) * 100;
      return null;
    })();

    const pace = resolveGoalPaceInfo(
      {
        key,
        gap: header?.gap ?? metric.gap ?? target - actual,
        gap_formatted: header?.gap_formatted || metric.gap_formatted,
        gap_text: header?.gap_text || metric.gap_text,
        daily_needed: header?.daily_needed ?? metric.daily_needed,
        daily_needed_formatted: header?.daily_needed_formatted || metric.daily_needed_formatted,
        daily_requirement_text: header?.daily_requirement_text || metric.daily_requirement_text,
        working_days_remaining:
          header?.working_days_remaining ??
          period.working_days_remaining ??
          metric.working_days_remaining,
      },
      { key, period },
    );

    const actionPlan = resolveActionPlan(metric, {
      action_plan:
        payload?.action_plan || month.action_plan || metric.action_plan || header?.action_plan,
      period,
      status: header?.status || month.status,
    });

    const monthName = resolveMonthName(period);
    const title =
      header?.title ||
      (money && monthName ? `هدف فروش ${monthName}` : header?.short_title || 'هدف من');

    const dropdownGoals = Array.isArray(payload?.my_goals_dropdown)
      ? payload.my_goals_dropdown
      : [];
    // اگر بک‌اند آیتم تیم را در dropdown نگذاشته بود، از team_header_goal بساز
    const hasTeamInDropdown = dropdownGoals.some(
      (g) => g?.is_team || g?.key === 'team_revenue' || g?.team_id != null,
    );
    const enrichedDropdown =
      hasTeam && !hasTeamInDropdown
        ? [
            ...dropdownGoals,
            {
              key: 'team_revenue',
              is_team: true,
              team_id: teamHeader.team_id || myTeam?.id,
              team_name: teamHeader.team_name || myTeam?.name || myTeam?.team_name,
              title: teamHeader.short_title || `هدف تیم (${teamHeader.team_name || 'فروش'})`,
              full_title: teamHeader.title,
              display_text: teamHeader.display_text,
              achievement_rate: teamHeader.achievement_rate,
              achievement_rate_display: teamHeader.achievement_rate_display,
              target: teamHeader.target_value,
              actual: teamHeader.actual_value,
              gap: teamHeader.gap,
              gap_formatted: teamHeader.gap_formatted,
              status: teamHeader.status,
              status_label: teamHeader.status_label,
              is_primary: false,
            },
          ]
        : dropdownGoals;

    return {
      header: header || {},
      hasPersonal,
      hasTeam,
      teamHeader,
      myTeam,
      key,
      money,
      actual,
      target,
      rate,
      projected,
      daysRemaining,
      daysPassed,
      daysTotal,
      timeElapsedPct,
      progressIndex,
      pace,
      actionPlan,
      monthName,
      title,
      statusLabel: header?.status_label || month.status_label,
      status: header?.status || month.status,
      dropdownGoals: enrichedDropdown,
      concurrentGoals: Array.isArray(payload?.concurrent_goals) ? payload.concurrent_goals : [],
      periodName: period.period_name_fa || period.period_type_fa || null,
    };
  }, [payload]);

  if (loading) {
    return (
      <div
        className={`
          flex items-center justify-center h-10 w-10 rounded-xl border-2
          ${
            isDarkMode
              ? 'bg-[#0b1220] border-[#2dd4bf]/60'
              : 'bg-white border-[#0D887E]/50 shadow-sm'
          }
        `}
      >
        <Spin size="small" />
      </div>
    );
  }

  if (!summary) return null;

  const {
    header,
    hasPersonal,
    hasTeam,
    teamHeader,
    money,
    actual,
    target,
    rate,
    projected,
    daysRemaining,
    timeElapsedPct,
    progressIndex,
    pace,
    actionPlan,
    monthName,
    title,
    statusLabel,
    dropdownGoals,
    concurrentGoals,
    periodName,
  } = summary;

  const workingDaysLeft = pace.workingDaysRemaining ?? (daysRemaining > 0 ? daysRemaining : null);

  const formatValue = (v) => (money ? formatReportMoneyFa(v) : formatReportNumberFa(v));
  const formatCompact = (v) => (money ? formatCompactMoney(v) : formatReportNumberFa(v));
  const rateDisplay = header.achievement_rate_display || formatRate(rate);
  const stroke = strokeForRate(rate);
  const teamRate = toNumber(teamHeader?.achievement_rate);
  const teamRateDisplay =
    teamHeader?.achievement_rate_display || (hasTeam ? formatRate(teamRate) : null);
  const teamStroke = strokeForRate(teamRate);
  const personalGoals = dropdownGoals.filter((g) => !g.is_team && g.key !== 'team_revenue');
  const teamGoals = dropdownGoals.filter((g) => g.is_team || g.key === 'team_revenue');

  const chip = (
    <button
      type="button"
      aria-label="هدف من"
      className={`
        group flex flex-col justify-center gap-0.5 max-w-[min(100vw,420px)]
        h-10 px-2.5 sm:px-3 rounded-xl text-xs font-medium custom-font
        transition-all duration-200 cursor-pointer outline-none
        focus-visible:ring-2 focus-visible:ring-offset-1
        ${
          isDarkMode
            ? `
              bg-[#0b1220] text-slate-50
              border-2 border-[#2dd4bf]
              shadow-[0_0_0_1px_rgba(45,212,191,0.25),0_4px_14px_rgba(0,0,0,0.35)]
              hover:bg-[#111827] hover:border-[#5eead4]
              focus-visible:ring-[#2dd4bf]/50
            `
            : `
              bg-white text-slate-800
              border-2 border-[#0D887E]
              shadow-[0_1px_2px_rgba(13,136,126,0.12),0_4px_12px_rgba(13,136,126,0.14)]
              hover:bg-[#f0fdfa] hover:border-[#0a6f67] hover:shadow-[0_2px_8px_rgba(13,136,126,0.22)]
              focus-visible:ring-[#0D887E]/35 focus-visible:ring-offset-white
            `
        }
      `}
    >
      <div className="flex items-center gap-1.5 min-w-0 w-full">
        <span
          className={`
            inline-flex items-center justify-center w-5 h-5 rounded-md shrink-0
            ${isDarkMode ? 'bg-[#0D887E]/30 text-[#5eead4]' : 'bg-[#0D887E] text-white'}
          `}
        >
          <AimOutlined className="text-[11px]" />
        </span>
        {hasPersonal ? (
          <>
            <span className={`shrink-0 font-bold ${isDarkMode ? 'text-[#5eead4]' : 'text-[#0D887E]'}`}>
              من
            </span>
            <span
              className={`tabular-nums font-bold shrink-0 ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              {rateDisplay}
            </span>
          </>
        ) : null}
        {hasPersonal && hasTeam ? (
          <span className={`opacity-35 ${isDarkMode ? 'text-slate-300' : 'text-slate-400'}`}>|</span>
        ) : null}
        {hasTeam ? (
          <>
            <TeamOutlined
              className={`text-[11px] shrink-0 ${isDarkMode ? 'text-[#5eead4]' : 'text-[#0D887E]'}`}
            />
            <span className={`shrink-0 font-bold ${isDarkMode ? 'text-[#5eead4]' : 'text-[#0D887E]'}`}>
              تیم
            </span>
            <span
              className={`tabular-nums font-bold shrink-0 ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              {teamRateDisplay}
            </span>
          </>
        ) : null}
        {hasPersonal ? (
          <span
            className={`hidden lg:inline tabular-nums text-[11px] truncate ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}
          >
            {formatCompact(actual)} / {formatCompact(target)}
          </span>
        ) : null}
        {workingDaysLeft != null && workingDaysLeft > 0 ? (
          <span
            className={`hidden xl:inline text-[10px] whitespace-nowrap ${
              isDarkMode ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            · {formatReportNumberFa(workingDaysLeft)} روز کاری
          </span>
        ) : null}
        <DownOutlined
          className={`text-[9px] transition-transform ${
            open ? 'rotate-180' : ''
          } ${isDarkMode ? 'text-[#5eead4]' : 'text-[#0D887E]'}`}
        />
      </div>
      <div className="w-full px-0.5 flex gap-1">
        {hasPersonal ? (
          <div
            className={`h-[3px] flex-1 rounded-full overflow-hidden ${
              isDarkMode ? 'bg-white/15' : 'bg-slate-200'
            }`}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(100, Math.max(0, rate))}%`,
                background: stroke,
              }}
            />
          </div>
        ) : null}
        {hasTeam ? (
          <div
            className={`h-[3px] flex-1 rounded-full overflow-hidden ${
              isDarkMode ? 'bg-white/15' : 'bg-slate-200'
            }`}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(100, Math.max(0, teamRate))}%`,
                background: teamStroke,
              }}
            />
          </div>
        ) : null}
      </div>
    </button>
  );

  const displayTitle = hasPersonal
    ? title
    : teamHeader?.title || teamHeader?.team_name || 'هدف تیم';
  const displayRate = hasPersonal ? rate : teamRate;
  const displayRateLabel = hasPersonal ? rateDisplay : teamRateDisplay;
  const displayStroke = hasPersonal ? stroke : teamStroke;
  const displayActual = hasPersonal ? actual : toNumber(teamHeader?.actual_value);
  const displayTarget = hasPersonal ? target : toNumber(teamHeader?.target_value);
  const displayStatusLabel = hasPersonal
    ? statusLabel
    : teamHeader?.status_label;
  const displayMoney = hasPersonal ? money : true;

  const formatDisplayValue = (v) =>
    displayMoney ? formatReportMoneyFa(v) : formatReportNumberFa(v);
  const formatDisplayCompact = (v) =>
    displayMoney ? formatCompactMoney(v) : formatReportNumberFa(v);

  const popoverContent = (
    <div
      className={`header-my-goal-popover-scroll custom-font custom-scrollbar w-[300px] sm:w-[340px] ${
        isDarkMode ? 'is-dark' : 'is-light'
      }`}
      dir="rtl"
    >
      {/* هدر پاپ‌اور */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="text-[11px] text-slate-400 mb-0.5">
            {hasPersonal ? 'خلاصه هدف جاری' : 'خلاصه هدف تیم'}
          </div>
          <div className="font-semibold text-slate-900 dark:text-slate-50 text-[15px] leading-snug">
            {displayTitle}
            {hasPersonal && monthName && !String(displayTitle).includes(monthName) ? (
              <span className="text-slate-400 font-normal text-sm"> · {monthName}</span>
            ) : null}
          </div>
          {periodName ? (
            <div className="text-[11px] text-slate-400 mt-0.5">{periodName}</div>
          ) : null}
        </div>
        {displayStatusLabel ? (
          <Tag
            color={displayRate >= 100 ? 'success' : displayRate >= 80 ? 'processing' : 'warning'}
            className="custom-font m-0 shrink-0 text-[10px]"
          >
            {displayStatusLabel}
          </Tag>
        ) : null}
      </div>

      {/* تحقق اصلی */}
      <div
        className={`rounded-xl px-3 py-3 mb-3 ${
          isDarkMode
            ? 'bg-[rgba(13,136,126,0.12)] border border-[rgba(13,136,126,0.25)]'
            : 'bg-gradient-to-br from-emerald-50 via-teal-50/50 to-white border border-emerald-100'
        }`}
      >
        <div className="flex items-center gap-3">
          <Progress
            type="circle"
            width={64}
            percent={Math.min(100, Math.max(0, displayRate))}
            strokeColor={displayStroke}
            trailColor={isDarkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}
            format={() => (
              <span className="tabular-nums text-[13px] font-bold text-slate-800 dark:text-slate-100">
                {displayRateLabel}
              </span>
            )}
          />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] text-slate-500 mb-0.5">محقق‌شده / هدف</div>
            <div className="tabular-nums text-sm font-semibold text-slate-900 dark:text-slate-50 leading-tight">
              {formatDisplayCompact(displayActual)}
              <span className="text-slate-400 font-normal"> / {formatDisplayCompact(displayTarget)}</span>
            </div>
            <div className="tabular-nums text-[10px] text-slate-400 mt-1 truncate">
              {formatDisplayValue(displayActual)} از {formatDisplayValue(displayTarget)}
            </div>
          </div>
        </div>
        <Progress
          className="mt-2.5 mb-0"
          percent={Math.min(100, Math.max(0, displayRate))}
          showInfo={false}
          strokeColor={displayStroke}
          size="small"
          trailColor={isDarkMode ? 'rgba(255,255,255,0.08)' : undefined}
        />
      </div>

      {/* برنامه اقدام / فاصله تا هدف */}
      {hasPersonal && actionPlan?.is_behind ? (
        <GoalActionPlanCard compact className="mb-3" actionPlan={actionPlan} />
      ) : hasPersonal && (pace.gapText || pace.dailyRequirementText) ? (
        <GoalPaceCallout
          className="mb-3"
          gapText={pace.gapText}
          dailyRequirementText={pace.dailyRequirementText}
          workingDaysRemaining={pace.workingDaysRemaining}
        />
      ) : null}

      {/* جزئیات عددی */}
      {hasPersonal ? (
      <div className="mb-1">
        <StatRow label="هدف" value={formatValue(target)} />
        <StatRow label="محقق‌شده" value={formatValue(actual)} emphasize />
        <StatRow label="تحقق" value={rateDisplay} emphasize />
        {timeElapsedPct != null ? (
          <StatRow label="زمان سپری‌شده" value={formatRate(timeElapsedPct, 0)} />
        ) : null}
        {progressIndex != null && Number.isFinite(progressIndex) ? (
          <StatRow label="شاخص نسبت به زمان" value={formatRate(progressIndex, 1)} />
        ) : null}
        {projected > 0 ? (
          <StatRow label="تخمین پایان بازه" value={formatValue(projected)} />
        ) : null}
      </div>
      ) : null}

      {/* اهداف دیگر + هدف تیم */}
      {personalGoals.filter((g) => !g.is_primary).length > 0 || teamGoals.length > 0 ? (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
          {personalGoals.filter((g) => !g.is_primary).length > 0 ? (
            <>
              <div className="text-[11px] font-medium text-slate-500 mb-2">اهداف فعال دیگر</div>
              <div className="space-y-2 mb-2">
                {personalGoals
                  .filter((g) => !g.is_primary)
                  .map((g) => {
                    const gRate = toNumber(g.achievement_rate);
                    const gPace = resolveGoalPaceInfo(g, { key: g.key });
                    return (
                      <div
                        key={g.key}
                        className="rounded-lg border border-slate-100 dark:border-slate-700 px-2.5 py-1.5"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[12px] font-medium text-slate-700 dark:text-slate-200 truncate">
                            {g.title || g.full_title}
                          </span>
                          <span className="tabular-nums text-[11px] font-semibold shrink-0">
                            {g.achievement_rate_display || formatRate(gRate)}
                          </span>
                        </div>
                        <Progress
                          className="mt-1 mb-0"
                          percent={Math.min(100, Math.max(0, gRate))}
                          size="small"
                          showInfo={false}
                          strokeColor={strokeForRate(gRate)}
                        />
                        {(gPace.gapText || gPace.dailyRequirementText) && (
                          <GoalPaceCallout
                            compact
                            className="mt-1.5"
                            gapText={gPace.gapText}
                            dailyRequirementText={gPace.dailyRequirementText}
                            workingDaysRemaining={null}
                          />
                        )}
                      </div>
                    );
                  })}
              </div>
            </>
          ) : null}

          {teamGoals.length > 0 ? (
            <>
              <div className="text-[11px] font-medium text-slate-500 mb-2 flex items-center gap-1">
                <TeamOutlined />
                هدف تیم
              </div>
              <div className="space-y-2">
                {teamGoals.map((g) => {
                  const gRate = toNumber(g.achievement_rate);
                  return (
                    <div
                      key={g.key || g.team_id || 'team'}
                      className="rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50/60 dark:bg-teal-950/30 px-2.5 py-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[12px] font-medium text-slate-700 dark:text-slate-200 truncate inline-flex items-center gap-1">
                          <TeamOutlined className="text-teal-600" />
                          {g.team_name || g.title || g.full_title || 'هدف تیم'}
                        </span>
                        <span className="tabular-nums text-[11px] font-semibold shrink-0 text-teal-700 dark:text-teal-300">
                          {g.achievement_rate_display || formatRate(gRate)}
                        </span>
                      </div>
                      <Progress
                        className="mt-1 mb-0"
                        percent={Math.min(100, Math.max(0, gRate))}
                        size="small"
                        showInfo={false}
                        strokeColor={strokeForRate(gRate)}
                      />
                      {g.gap_formatted || g.gap_text ? (
                        <div className="text-[10px] text-slate-500 mt-1">
                          {g.gap_text || `باقیمانده: ${g.gap_formatted}`}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {/* اهداف هم‌زمان در بازه‌های مختلف */}
      {concurrentGoals.length > 1 ? (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
          <div className="text-[11px] font-medium text-slate-500 mb-2">
            اهداف فعال در بازه‌های مختلف — برای جابه‌جایی کلیک کنید
          </div>
          <div className="space-y-1.5">
            {concurrentGoals.map((g) => (
              <button
                key={g.id}
                type="button"
                disabled={switchingGoal || g.is_current_view}
                onClick={() => switchConcurrentGoal(g.id)}
                className={`w-full text-right rounded-lg px-2.5 py-1.5 border transition-colors ${
                  g.is_current_view
                    ? 'border-teal-300 bg-teal-50/70 dark:bg-teal-950/30 dark:border-teal-700 cursor-default'
                    : 'border-slate-100 dark:border-slate-700 hover:border-teal-300 hover:bg-teal-50/40 dark:hover:bg-teal-950/20 cursor-pointer'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12px] font-medium text-slate-700 dark:text-slate-200 truncate">
                    {g.period_type_fa || g.period_name_fa || g.period_key}
                    {g.is_current_view ? (
                      <span className="text-[10px] text-teal-600 mr-1"> (جاری)</span>
                    ) : null}
                  </span>
                  <span className="tabular-nums text-[11px] font-semibold shrink-0">
                    {formatRate(g.revenue_achievement_rate)}
                  </span>
                </div>
                <div className="text-[10px] text-slate-400 tabular-nums truncate">
                  {g.start_jalali && g.end_jalali
                    ? `${String(g.start_jalali).slice(0, 10)} تا ${String(g.end_jalali).slice(0, 10)}`
                    : g.period_name_fa || ''}
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div
        className={`pt-3 mt-3 border-t sticky bottom-0 pb-0.5 -mx-1 px-1 ${
          isDarkMode
            ? 'border-slate-700 bg-[#0f172a]'
            : 'border-slate-100 bg-white'
        }`}
      >
        <Button
          type="primary"
          block
          className="custom-font h-9 font-medium"
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            setOpen(false);
            navigate('/leads/reports?tab=goals');
          }}
        >
          مشاهده جزئیات هدف
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .header-my-goal-popover.ant-popover {
          z-index: 1200 !important;
          max-width: calc(100vw - 16px);
        }
        .header-my-goal-popover .ant-popover-content,
        .header-my-goal-popover .ant-popover-container,
        .header-my-goal-popover .ant-popover-inner,
        .header-my-goal-popover .ant-popover-inner-content {
          padding: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
          max-height: none !important;
          overflow: visible !important;
        }
        .header-my-goal-popover-scroll {
          box-sizing: border-box !important;
          padding: 16px !important;
          border-radius: 14px !important;
          max-height: calc(100vh - 96px) !important;
          max-height: calc(100dvh - 96px) !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
          pointer-events: auto;
          border: 1px solid rgba(13, 136, 126, 0.18);
          box-shadow: 0 12px 40px rgba(13, 136, 126, 0.14), 0 4px 16px rgba(0, 0, 0, 0.08);
        }
        .header-my-goal-popover-scroll.is-light {
          background: #ffffff !important;
          color: #0f172a;
        }
        .header-my-goal-popover-scroll.is-dark {
          background: #0f172a !important;
          color: #f8fafc;
          border-color: rgba(45, 212, 191, 0.28);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
        }
      `}</style>
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        placement="bottomLeft"
        arrow={false}
        content={popoverContent}
        overlayClassName="header-my-goal-popover"
        getPopupContainer={() => document.body}
        destroyOnHidden
        overlayStyle={{ zIndex: 1200 }}
        styles={{
          body: {
            padding: 0,
            background: 'transparent',
            boxShadow: 'none',
          },
          root: {
            zIndex: 1200,
          },
        }}
      >
        {chip}
      </Popover>
    </>
  );
};

export default HeaderMyGoalWidget;
